import type { PermissionMap } from './rbac';

export type Id = string;
/**
 * The one UUID shape both sides accept (R15): exactly zod 4's `z.uuid()` — an RFC 9562 version 1–8 and variant, or the
 * nil and max ids. A client that sends only ids matching it never gets a 422 from a server that parses with `z.uuid()`.
 */
export const UUID_RE =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/;
export type ISODate = string;
export type Month = string;
export type Version = number;
export type Snapshot = { id: Id; name: string; kind: string };
export type Actor = { kind: 'user'; id: Id; name: string } |
  { kind: 'system'; id: 'bootstrap' | 'operator' | 'scheduler' | 'cleanup'; name: string };
/**
 * The refusal envelope. `openCount`, `rootId` and `rootTitle` are the Workplace tree payloads of spec §5.4
 * (`OPEN_CHILDREN`, `TREE_DONE`); `openCount` is also the open work that refuses removing a sharing member
 * (`MEMBER_HAS_WORK`). Projects & stakeholders add four: `projectCount`/`openItemCount` say what still
 * uses a partner or a project that refuses a delete, `existing` names the live partner a duplicate name collides
 * with, and `mergedInto` tells a read of a gone partner where it was merged (`null`: deleted, not merged).
 * S5 adds three: `rows` names the batch rows `BATCH_CHANGED` marked (P19), `rowCount` and `split` are a sheet's data
 * rows and the 500-row ranges `INGEST_TOO_LARGE` offers to split it into (D61). S6 adds `names`: the tools whose
 * version changed under an approval (`TOOL_CHANGED`, D23). S7 adds `hint` and `missionId`: an undo of `mission.create`
 * refused `UNDO_STALE` names the mission so the card can offer "Hủy nhiệm vụ" (D115). Every other code leaves them out.
 */
export type ApiError = { error: { code: string; message: string;
  fields?: Record<string, string>; currentVersion?: Version;
  openCount?: number; rootId?: Id; rootTitle?: string;
  projectCount?: number; openItemCount?: number; existing?: { id: Id; name: string }; mergedInto?: Id | null;
  rows?: { id: Id; reasons: string[] }[]; rowCount?: number; split?: [number, number][];
  names?: string[]; hint?: 'cancel_mission'; missionId?: Id } };

/** Every refusal payload key beside `code`, `message`, `fields` and `currentVersion`, in one list. */
export const ERROR_EXTRA_KEYS = [
  'openCount', 'rootId', 'rootTitle', 'projectCount', 'openItemCount', 'existing', 'mergedInto', 'rows', 'rowCount', 'split',
  'names', 'hint', 'missionId',
] as const;
export type ErrorExtraKey = typeof ERROR_EXTRA_KEYS[number];
export type ErrorExtra = Pick<ApiError['error'], ErrorExtraKey>;
export type Page<T> = { items: T[]; nextCursor: string | null };
/**
 * Who is acting, and what they may do. `permissions` is the merge of the account's live role assignments
 * (`mergeGrants`, `@domain/rbac`); `isAdmin` says it holds the system role.
 */
export type Principal = { companyId: Id; userId: Id; name: string; version: Version;
  permissions: PermissionMap; isAdmin: boolean };
/**
 * Every audit action the server writes. `AuditInput.action` is typed over this list, so a new action
 * does not compile until it is listed here — and the web's label table is typed over the same list, so it
 * does not compile until the action has a Vietnamese label either. Rows written before an action was
 * renamed still come back as plain strings (`AuditEventDTO.action`) and are shown as the raw code.
 */
export const AUDIT_ACTIONS = [
  'tasks.task.create', 'tasks.task.update', 'tasks.task.delete',
  'tasks.external_party.create', 'tasks.external_party.update', 'tasks.external_party.delete',
  'tasks.comment.create', 'tasks.comment.update', 'tasks.comment.delete',
  'tasks.attachment.attached', 'tasks.attachment.deleted',
  'tasks.upload.policy', 'tasks.upload.created', 'tasks.upload.verified', 'tasks.upload.attached',
  'tasks.upload.expired', 'tasks.upload.orphan_cleanup_marked', 'tasks.upload.orphan_cleaned',
  'tasks.upload.orphan_swept',
  // Workplace (spec §10): every new write of the former tasks module is recorded under these names. The
  // `tasks.*` names above stay for the rows written before the rename.
  'workplace.item.create', 'workplace.item.update', 'workplace.item.delete',
  'workplace.item.move', 'workplace.item.type_change', 'workplace.item.complete_tree',
  'workplace.external_party.create', 'workplace.external_party.update', 'workplace.external_party.delete',
  'workplace.comment.create', 'workplace.comment.update', 'workplace.comment.delete',
  'workplace.attachment.attached', 'workplace.attachment.deleted',
  'workplace.upload.policy', 'workplace.upload.created', 'workplace.upload.verified',
  'workplace.upload.attached', 'workplace.upload.expired',
  'workplace.upload.orphan_cleanup_marked', 'workplace.upload.orphan_cleaned',
  'workplace.upload.orphan_swept',
  // Spaces (010): a Space's life and its membership. A promotion by the admin is a `member_update` too.
  'workplace.space.create', 'workplace.space.update', 'workplace.space.delete',
  'workplace.space.member_add', 'workplace.space.member_update', 'workplace.space.member_remove',
  // Projects & stakeholders (spec §5.5): a work item's project and stakeholders.
  'workplace.item.stakeholder_add', 'workplace.item.stakeholder_remove', 'workplace.item.project_set',
  // AI S5 D10: a card ↔ finance entry link, recorded on the card.
  'workplace.item.finance_link', 'workplace.item.finance_unlink',
  // AI S6 §8: a card's outside-source chip, made or removed, recorded on the card (server id and URL, never a title).
  'workplace.item.external_link', 'workplace.item.external_unlink',
  // Sharing (spec §8.6): a home's mode, its members and its owner, and an item moving to another home.
  'workplace.sharing.mode_change', 'workplace.sharing.member_add', 'workplace.sharing.member_remove',
  'workplace.sharing.owner_change', 'workplace.item.home_change',
  'projects.sharing.mode_change', 'projects.sharing.member_add', 'projects.sharing.member_remove',
  'projects.sharing.owner_change',
  // AI S9 §4.3: every delegation signal but `progress_asked` (which the assistant's contact log records), in
  // DELEGATION_SIGNAL_KINDS order.
  'workplace.delegation.delegated', 'workplace.delegation.accepted', 'workplace.delegation.deadline_requested',
  'workplace.delegation.deadline_accepted', 'workplace.delegation.deadline_declined',
  'workplace.delegation.deadline_withdrawn', 'workplace.delegation.question', 'workplace.delegation.clarified',
  'workplace.delegation.tracking_off', 'workplace.delegation.tracking_on', 'workplace.delegation.restored',
  // The partner directory: a partner's life, its merge, and its contacts (recorded on the partner).
  'partners.partner.create', 'partners.partner.update', 'partners.partner.delete', 'partners.partner.merge',
  'partners.contact.create', 'partners.contact.update', 'partners.contact.delete',
  // A project, and its stakeholders, participants and contracts (all recorded on the project).
  'projects.project.create', 'projects.project.update', 'projects.project.delete',
  'projects.stakeholder.add', 'projects.stakeholder.update', 'projects.stakeholder.remove',
  'projects.participant.add', 'projects.participant.update', 'projects.participant.remove',
  'projects.contract.add', 'projects.contract.update', 'projects.contract.remove',
  'strategy.objective.create', 'strategy.objective.update', 'strategy.objective.delete',
  'strategy.priority.create', 'strategy.priority.update', 'strategy.priority.delete',
  'strategy.metric.create', 'strategy.metric.update', 'strategy.metric.delete',
  'strategy.review.create', 'strategy.review.update', 'strategy.review.delete',
  'finance.unit.create', 'finance.unit.update', 'finance.period.create', 'finance.period.update',
  // Finance rebuild (spec §7): a project's budget, entries and their actuals, recurring templates and their sync,
  // categories, the opening balance and reconciliations, the alert threshold. The four names above stay for old rows.
  'finance.project.update',
  'finance.entry.create', 'finance.entry.update', 'finance.entry.actual_record', 'finance.entry.actual_clear',
  'finance.entry.delete',
  'finance.recurring.create', 'finance.recurring.update', 'finance.recurring.stop', 'finance.recurring.resume',
  'finance.recurring.delete', 'finance.recurring.sync',
  'finance.category.create', 'finance.category.update',
  'finance.balance.opening_set', 'finance.balance.opening_update', 'finance.balance.reconcile',
  'finance.threshold.update',
  'invitation.issued', 'invitation.accepted', 'invitation.revoked', 'invitation.resent', 'invitation.expired',
  'access_reset.issued', 'access_reset.used', 'access_reset.revoked', 'access_reset.expired',
  'account.updated', 'account.password_changed',
  // RBAC (spec §8): a role's life and its assignments, and the one-off switch migration 013 records per company.
  'role.create', 'role.update', 'role.delete',
  'role_assignment.create', 'role_assignment.update', 'role_assignment.revoke',
  'rbac.migrated',
  // The assistant's configuration (AI S1 §9): providers from P1 Task 7, settings from Task 8.
  'ai.provider.create', 'ai.provider.update', 'ai.provider.delete', 'ai.settings.update',
  // S7 §9.2 (D19): the company's autonomy ceilings and the missions switch.
  'ai.autonomy.update',
  // Outside MCP servers Hana calls (S6 §6.1, D16): the company's catalogue.
  'ai.ext_server.create', 'ai.ext_server.update', 'ai.ext_server.delete', 'ai.ext_tools.update',
  // A person's connection to one of them (S6 §6.2, §6.4): made (or made again), and revoked, with the reason.
  'ai.ext_connection.create', 'ai.ext_connection.revoke',
  // One `ext.call` Hana sent through such a connection (S6 §12.3): who, where, which tool, how it ended; never its args.
  'ai.ext_tool.call',
  // Outside AI connections over MCP (S2 §7.6, D34): the company's MCP settings, and a connection made or revoked.
  'ai.mcp.update', 'ai.mcp.connection.create', 'ai.mcp.connection.revoke',
  // Proactive Hana (S3 §5.2): the "Chủ động" tab, the company knowledge an admin keeps, and a nudge sent to someone
  // about a task (recorded on the task, sender and recipient only, never the note).
  'ai.proactive.update', 'ai.memory.company.create', 'ai.memory.company.update', 'ai.memory.company.delete',
  'work.nudge',
  // The admin tab "Bóc tài liệu" (S5 §11.3): its two switches.
  'ai.ingest.update',
  // Document ingestion (S5 §11.5, D77): a batch's life. Each record it writes keeps its module's own event, via Hana.
  'assistant.batch.created', 'assistant.batch.applied', 'assistant.batch.undone', 'assistant.batch.discarded',
  'assistant.batch.expired',
  // A mission's owner on its page (S7 §9.2): the answer, pause/resume/cancel, "Chạy ngay", the settings, the delete.
  'assistant.mission.answered', 'assistant.mission.paused', 'assistant.mission.resumed', 'assistant.mission.cancelled',
  'assistant.mission.run', 'assistant.mission.settings', 'assistant.mission.deleted',
  // A standing order (S7 §7.1, P14), on its page or approved in chat: made, edited, turned on or off, deleted, and
  // "Chạy thử ngay".
  'assistant.order.created', 'assistant.order.updated', 'assistant.order.enabled', 'assistant.order.disabled',
  'assistant.order.deleted', 'assistant.order.run',
] as const;
export type AuditAction = typeof AUDIT_ACTIONS[number];
export const AUDIT_MODULES = ['identity', 'tasks', 'workplace', 'strategy', 'finance', 'partners', 'projects', 'ai'] as const;
export type AuditModule = typeof AUDIT_MODULES[number];
/** The channels an audited write may come through besides a person's own click (D44): the assistant, or an outside AI
 *  connection over MCP (S2). 022's `audit_events_via_check` lists exactly these; `database-guards` compares the two, the
 *  way `rbac-migration` compares the permission CHECK. */
export const VIA_CHANNELS = ['assistant', 'mcp'] as const;
export type ViaChannel = (typeof VIA_CHANNELS)[number];
/** How an audited write was made when it was not the person's own click (spec §5.5). Stored whole in audit_events.via.
 *  `persona` stays required in TS: every S1 writer is the assistant and names one. The DB CHECK allows it absent (D44). */
export type AuditVia = { channel: ViaChannel; conversationId: Id | null; actionId: Id; persona: string;
  /** The MCP connection the write came through (S2 §7.6, D55); null for the assistant. Rows stored before S2 P2 have no
   *  key. Never read by `toAuditViaDTO`: no reader, the owner included, ever receives it. */
  connectionId: Id | null;
  /** The background run that made the write (S7 D94); absent for a chat turn and on rows stored before it. The reader
   *  resolves the run's mission or order from this id, so the link outlives the run conversation's 30-day purge. */
  runId?: Id };
/** What readers see (D94, S8 §6.1, D38). `assisted` says the write came through an assistant, to everyone. `persona`
 *  (the name the actor's assistant carried, or the MCP connection's) is in the payload only for the row's own actor,
 *  or on the Audit page (`surface: 'audit'`) for an `audit.view` holder; anywhere else the key is absent, so another
 *  person reads "Bình ✦". `own` (the conversation and action ids) is the actor's alone, and only when the write came
 *  from a conversation. A background run's conversation never opens in the chat panel (S7 §1.2 item 21, §8.1): its
 *  owner gets `run` and the mission or the standing order the run belonged to, when that run is still there. */
export type AuditViaDTO = { channel: ViaChannel; assisted: true; persona?: string;
  own: { conversationId: Id; actionId: Id; run?: true; missionId?: Id; orderId?: Id } | null };
/** Who reads a via (S8 §6.1): the person, and whether they hold `audit.view`. */
export type ViaViewer = { userId: Id; auditView: boolean };
/** `'audit'` is the Audit page's `GET /api/activity` alone; every other reader (histories, the board, the assistant's
 *  `activity.recent` in app or over MCP) is `'feed'`. */
export type ViaSurface = 'audit' | 'feed';
/** What a run (keyed by its id, D94) belonged to (S7 P24). A run that is gone has no entry. */
export type RunOwner = { missionId: Id | null; orderId: Id | null };
/** The one filter every server path that sends a stored `via` to a reader goes through (S8 §6.1). */
export const toAuditViaDTO = (via: AuditVia | null, viewer: ViaViewer | null, actor: Actor, surface: ViaSurface,
  runOwners?: ReadonlyMap<Id, RunOwner>): AuditViaDTO | null => {
  if (via === null) return null;
  const isActor = actor.kind === 'user' && viewer !== null && actor.id === viewer.userId;
  // D44: a via may be stored without a persona; then no reader gets the key.
  const seesPersona = typeof via.persona === 'string'
    && (isActor || (surface === 'audit' && viewer !== null && viewer.auditView));
  const head = { channel: via.channel, assisted: true as const, ...(seesPersona ? { persona: via.persona } : {}) };
  const conversationId = via.conversationId;
  if (!isActor || conversationId === null) return { ...head, own: null };
  const owner = via.runId === undefined ? undefined : runOwners?.get(via.runId);
  const run = via.runId === undefined ? {} : {
    run: true as const,
    ...(owner === undefined || owner.missionId === null ? {} : { missionId: owner.missionId }),
    ...(owner === undefined || owner.orderId === null ? {} : { orderId: owner.orderId }),
  };
  return { ...head, own: { conversationId, actionId: via.actionId, ...run } };
};
export type AuditInput = { module: AuditModule;
  action: AuditAction; entity: Snapshot; before: Record<string, unknown> | null;
  after: Record<string, unknown> | null; references: Snapshot[] };
export type Clock = { now(): Date };
export type HttpMethod = 'POST'|'PUT'|'PATCH'|'DELETE';

/**
 * What became of one idempotent write, asked for by the id the browser sent. A save whose answer was lost
 * leaves the person unable to tell a duplicate from a retry, and guessing is how one task becomes two; this
 * is the only honest way to find out. `not_found` means exactly that — not "it failed": a request can still
 * be in flight, so it is never permission to send a different payload.
 */
export type RequestLookup<T = unknown> =
  | { state: 'committed'; route: string; data: T }
  | { state: 'not_found' };
